import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { duration } from 'dayjs';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@webapp/common/components/button';
import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { Loader } from '@webapp/common/components/loader';
import { CssQuestionType } from '@webapp/common/components/survey-custom';
import { TimeLeft } from '@webapp/common/components/time-left';
import { FIGMA_CLIENT_ID } from '@webapp/common/conf';
import { normalizeApiResponse } from '@webapp/common/lib/api';
import { ColorScheme } from '@webapp/common/lib/ui';
import type { SurveyStore } from '@webapp/common/resources/entities';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import { surveyApi } from '@webapp/survey/src/machine/api';
import { selectCurrentBundle, selectInfo } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import { FigmaEmbedViewer } from './FigmaEmbedViewer';

import css from './figma-test.css';

// В режиме «ответ по клику» обёртка бандлов имеет transform: translateY(...),
// из-за которого position: fixed внутри позиционируется относительно неё,
// а не viewport. На время fullscreen figma-вопроса transform отключается
// через этот класс на body (см. survey.css).
const FIGMA_FULLSCREEN_BODY_CLASS = 'figmaTestFullscreenActive';

export interface FigmaScreen {
    id: string;
    name: string;
    imageUrl: string;
    // Размеры viewport'а фрейма (для embed'а).
    width?: number;
    height?: number;
    // Размеры контента на экспортированной картинке (для экранов со скроллом
    // больше viewport'а). Есть только у прототипов, выгруженных новой версией плагина.
    contentWidth?: number;
    contentHeight?: number;
    // Вложенные скролл-фреймы (карусели и т.п.) с позициями относительно экрана.
    // Нужны для перевода координат клика из системы вложенного фрейма в систему
    // экрана (Figma присылает клик относительно nearestScrollingFrameId).
    nestedFrames?: Array<{ id: string; x: number; y: number; width: number; height: number }>;
    figmaNodeId?: string;
}

export interface FigmaTransition {
    fromScreen: string;
    toScreen: string;
    trigger: 'click' | 'ON_CLICK' | 'ON_HOVER' | string;
    hotspot: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
}

export interface FigmaHotspot {
    id: string;
    frame: string;
    x: number;
    y: number;
    w: number;
    h: number;
    target: string | null;
}

export interface FigmaTestData {
    screens: Array<FigmaScreen>;
    transitions: Array<FigmaTransition>;
    startScreen: string;
    targetScreens: Array<string> | null;
    displayMode: 'fitWidth' | 'original' | 'fitHeight';
    timerSeconds: number | null;
    recordScreen: boolean;
    recordAudio: boolean;
    recordCamera: boolean;
    // Метаданные Figma для canvas-based рендеринга через embed.
    figmaFileId?: string;
    figmaStartNodeId?: string;
    figmaFileName?: string;
}

export interface FigmaEvent {
    type: 'click' | 'transition' | 'start' | 'end' | 'surrender';
    screenId: string;
    screenName?: string;
    x?: number;
    y?: number;
    timestampMs: number;
}

export interface FigmaPathStep {
    screen: string;
    name: string;
}

/**
 * Преобразует transitions из формата бэкенда в hotspots, которые удобно использовать
 * поверх Figma embed (как в easytest-pathway).
 */
const transitionsToHotspots = (transitions: Array<FigmaTransition>): Array<FigmaHotspot> => {
    return transitions.map((t, index) => ({
        id: `${t.fromScreen}->${t.toScreen}@${index}`,
        frame: t.fromScreen,
        x: t.hotspot.x,
        y: t.hotspot.y,
        w: t.hotspot.width,
        h: t.hotspot.height,
        target: t.toScreen,
    }));
};

const DEFAULT_PROTOTYPE_WIDTH = 375;
const DEFAULT_PROTOTYPE_HEIGHT = 812;

const normalizeFigmaTestData = (rawData: AnyObject): FigmaTestData | null => {
    const data = normalizeApiResponse(rawData);

    const rawScreens = data.screens as Array<FigmaScreen> | undefined;

    if (data.error !== 0 || !Array.isArray(rawScreens) || rawScreens.length === 0) {
        return null;
    }

    const screens = rawScreens.map((screen) => ({
        ...screen,
        figmaNodeId: screen.figmaNodeId || screen.id,
    }));

    const screenIds = new Set<string>(screens.map((screen) => screen.id));
    const rawTransitions = (data.transitions || []) as Array<FigmaTransition>;

    const transitions = rawTransitions.filter(
        (t) => screenIds.has(t.fromScreen) && screenIds.has(t.toScreen) && t.trigger === 'ON_CLICK',
    );

    const startScreen = (data.startScreen as string | undefined) || screens[0].id;
    const targetScreens = data.targetScreens as Array<string> | null | undefined;
    const displayMode =
        (data.figmaDisplayMode as FigmaTestData['displayMode'] | undefined) ||
        (data.displayMode as FigmaTestData['displayMode'] | undefined) ||
        'fitWidth';
    const timerSeconds = (data.timerSeconds as number | null | undefined) ?? null;
    const recordScreen = (data.recordScreen as boolean | undefined) || false;
    const recordAudio = (data.recordAudio as boolean | undefined) || false;
    const recordCamera = (data.recordCamera as boolean | undefined) || false;
    const figmaFileId = (data.figmaFileId as string | undefined) || undefined;
    const figmaStartNodeId = (data.figmaStartNodeId as string | undefined) || undefined;
    const figmaFileName = (data.figmaFileName as string | undefined) || undefined;

    return {
        screens,
        transitions,
        startScreen,
        targetScreens: targetScreens || null,
        displayMode,
        timerSeconds,
        recordScreen,
        recordAudio,
        recordCamera,
        figmaFileId,
        figmaStartNodeId,
        figmaFileName,
    };
};

export const FigmaTest: FC<SurveyQuestion> = ({ question, actor, readOnly, questionBlockPosition }) => {
    const { id: questionId } = question;
    const info = useStateSelector(selectInfo);
    const a = info.params.alert;
    const elementColor = info.design?.fonts?.elementColor;
    const darkText = info.design?.fonts?.buttonTextColor === ColorScheme.DARK;
    // Скругление кнопок из кастомизации дизайна (как у кнопки «Отправить»),
    // чтобы таймер выглядел так же, как кнопки рядом с ним.
    const { button: { root: { borderRadius: buttonBorderRadius = '' } = {} } = {} } = useContext(CustomStylesCtx);
    const timer = useSelector(actor, (snapshot) => snapshot.context.timer);
    const handleTimerExpiredRef = useRef<() => void>(() => {});
    const [testData, setTestData] = useState<FigmaTestData | null>(null);
    const [currentScreenId, setCurrentScreenId] = useState<string | null>(null);
    const [events, setEvents] = useState<Array<FigmaEvent>>([]);
    const [path, setPath] = useState<Array<FigmaPathStep>>([]);
    const [timerLeft, setTimerLeft] = useState<number | null>(null);
    const timerLeftDuration = useMemo(() => duration((timerLeft ?? 0) * 1000), [timerLeft]);
    const [isCompleted, setIsCompleted] = useState(false);
    const [completionStatus, setCompletionStatus] = useState<'success' | 'surrender' | 'timeout' | 'complete' | null>(
        null,
    );
    const [isSavingMedia, setIsSavingMedia] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hasStarted, setHasStarted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isEmbedLoading, setIsEmbedLoading] = useState(!readOnly);
    const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);
    const [permissionsError, setPermissionsError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
    const startTimeRef = useRef<number>(0);
    const pendingTimerStartRef = useRef(false);
    const mediaRecordersRef = useRef<Array<{ recorder: MediaRecorder; blobParts: Array<Blob>; type: string }>>([]);
    const isCompletedRef = useRef(false);
    const currentScreenIdRef = useRef<string | null>(null);
    const completeTestRef = useRef<
        ((success: boolean | null, finalScreenId: string, finalScreenName: string) => Promise<void>) | null
    >(null);
    const clickRecordedForTransitionRef = useRef<{ toScreen: string; clickX: number; clickY: number } | null>(null);
    const lastClickOnScreenRef = useRef<{ screenId: string; timestamp: number } | null>(null);
    const eventsRef = useRef<Array<FigmaEvent>>(events);
    const pathRef = useRef<Array<{ screen: string; name: string }>>(path);
    const autoAdvanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        currentScreenIdRef.current = currentScreenId;
    }, [currentScreenId]);

    useEffect(() => {
        isCompletedRef.current = isCompleted;
    }, [isCompleted]);

    useEffect(() => {
        eventsRef.current = events;
    }, [events]);

    useEffect(() => {
        pathRef.current = path;
    }, [path]);

    // Сбрасываем запланированный автопереход при размонтировании.
    useEffect(() => {
        return () => {
            if (autoAdvanceTimeoutRef.current) {
                clearTimeout(autoAdvanceTimeoutRef.current);
            }
        };
    }, []);

    // Помечаем body в fullscreen, чтобы survey.css мог отключить transform
    // обёртки бандлов (иначе fixed-элементы уезжают за пределы экрана).
    useEffect(() => {
        if (!isFullscreen) return;
        document.body.classList.add(FIGMA_FULLSCREEN_BODY_CLASS);
        return () => {
            document.body.classList.remove(FIGMA_FULLSCREEN_BODY_CLASS);
        };
    }, [isFullscreen]);

    // Запускаем таймер, когда прототип загрузился (onLoad или fallback-таймаут).
    useEffect(() => {
        if (!isEmbedLoading && pendingTimerStartRef.current) {
            pendingTimerStartRef.current = false;
            timer?.send({ type: 'START' });
        }
    }, [isEmbedLoading, timer]);

    // Подписываемся на общий таймер для отображения оставшегося времени
    // и для обработки истечения времени.
    useEffect(() => {
        if (!timer) return;
        const initialSnapshot = timer.getSnapshot();
        const initialLeftMs = Math.max(0, initialSnapshot.context.duration * 1000 - initialSnapshot.context.timeAgo);
        setTimerLeft(Math.ceil(initialLeftMs / 1000));
        const sub = timer.subscribe((snapshot) => {
            const leftMs = Math.max(0, snapshot.context.duration * 1000 - snapshot.context.timeAgo);
            setTimerLeft(Math.ceil(leftMs / 1000));
            if (leftMs === 0) {
                handleTimerExpiredRef.current?.();
            }
        });
        return () => sub.unsubscribe();
    }, [timer]);

    // Следим за реальной шириной/высотой родителя .figmaTest, чтобы учитывать padding'и и margin'и.
    // Нельзя измерять сам .figmaTest: при широком iframe он тоже растянется, и получится цикл.
    useEffect(() => {
        const container = containerRef.current;
        const parent = container?.parentElement;
        if (!container || !parent) return;

        const getContentSize = (el: HTMLElement) => {
            const style = window.getComputedStyle(el);
            const paddingLeft = parseFloat(style.paddingLeft) || 0;
            const paddingRight = parseFloat(style.paddingRight) || 0;
            const paddingTop = parseFloat(style.paddingTop) || 0;
            const paddingBottom = parseFloat(style.paddingBottom) || 0;

            return {
                width: el.clientWidth - paddingLeft - paddingRight,
                height: el.clientHeight - paddingTop - paddingBottom,
            };
        };

        const updateSize = () => {
            setContainerSize(getContentSize(parent));
        };

        updateSize();

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(updateSize);
            resizeObserver.observe(parent);
        }

        window.addEventListener('resize', updateSize);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateSize);
        };
    }, []);

    // Реальные размеры прототипа (получаем из imageUrl текущего экрана).
    const [prototypeSize, setPrototypeSize] = useState<{ width: number; height: number }>({
        width: DEFAULT_PROTOTYPE_WIDTH,
        height: DEFAULT_PROTOTYPE_HEIGHT,
    });

    const isGoalBased = Boolean(testData?.targetScreens && testData.targetScreens.length > 0);

    /**
     * Применяем targetScreens из question params, если они заданы в редакторе.
     */
    const mergeTargetScreens = useCallback(
        (data: FigmaTestData): FigmaTestData => {
            const hasTargetScreens = Boolean(question.params?.figmaTargetScreens);
            if (!hasTargetScreens) return data;

            try {
                const parsed = JSON.parse(question.params.figmaTargetScreens);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return {
                        ...data,
                        targetScreens: parsed,
                    };
                }
            } catch {
                // ignore
            }

            return data;
        },
        [question.params?.figmaTargetScreens],
    );

    // Загрузка данных теста
    useEffect(() => {
        let cancelled = false;

        surveyApi
            .startFigmaTest(questionId)
            .then((rawData) => {
                const data = normalizeFigmaTestData(rawData);
                if (cancelled) return;
                if (!data) {
                    setError(a.figmaErrorText);
                    setIsLoading(false);
                    return;
                }
                const mergedData = mergeTargetScreens(data);

                // displayMode должен приходить из настроек вопроса (params),
                // а не из start/figma_test, который может отдавать свой дефолт.
                const displayModeFromParams = question.params?.figmaDisplayMode as string | undefined;
                const isValidDisplayMode = (value: string | undefined): value is FigmaTestData['displayMode'] =>
                    value === 'fitWidth' || value === 'fitHeight' || value === 'original';
                const finalData: FigmaTestData = {
                    ...mergedData,
                    displayMode: isValidDisplayMode(displayModeFromParams)
                        ? displayModeFromParams
                        : mergedData.displayMode,
                    recordScreen: question.params?.figmaRecordScreen ?? mergedData.recordScreen,
                    recordAudio: question.params?.figmaRecordAudio ?? mergedData.recordAudio,
                    recordCamera: question.params?.figmaRecordCamera ?? mergedData.recordCamera,
                };

                setTestData(finalData);
                setCurrentScreenId(mergedData.startScreen);
                setIsLoading(false);
            })
            .catch((_e) => {
                if (cancelled) return;
                setError(a.figmaErrorText);
                setIsLoading(false);
            });

        return () => {
            cancelled = true;
            timer?.send({ type: 'STOP' });
            stopMediaRecording();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionId, readOnly]);

    // Определяем реальные размеры прототипа по текущему экрану.
    useEffect(() => {
        if (!testData || !currentScreenId) return;

        const screen = testData.screens.find((s) => s.id === currentScreenId);
        if (!screen) return;

        // Если бэкенд уже отдаёт размеры — используем их.
        if (screen.width && screen.height && screen.width > 0 && screen.height > 0) {
            setPrototypeSize({ width: screen.width, height: screen.height });
            return;
        }

        // Иначе пытаемся получить размеры из изображения.
        if (!screen.imageUrl) return;

        const img = new Image();
        img.onload = () => {
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setPrototypeSize({ width: img.naturalWidth, height: img.naturalHeight });
            }
        };
        img.src = screen.imageUrl;
    }, [testData, currentScreenId]);

    // Натуральные размеры экспортированного изображения текущего экрана.
    // Для экранов с внутренним скроллом изображение содержит весь контент
    // и больше viewport'а фрейма — эти размеры нужны вьюверу, чтобы считать
    // координаты клика в процентах от изображения (именно поверх него клики
    // рисуются в результатах).
    const [screenImageSize, setScreenImageSize] = useState<{ width: number; height: number } | null>(null);

    useEffect(() => {
        setScreenImageSize(null);

        if (!testData || !currentScreenId) return;

        const screen = testData.screens.find((s) => s.id === currentScreenId);
        if (!screen?.imageUrl) return;

        const img = new Image();
        img.onload = () => {
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setScreenImageSize({ width: img.naturalWidth, height: img.naturalHeight });
            }
        };
        img.src = screen.imageUrl;
    }, [testData, currentScreenId]);

    // Масштаб, с которым бэкенд экспортирует изображения экранов (обычно @2x).
    // Вычисляем по экранам без скролла: у них бэкенд-размеры совпадают с размерами
    // контента, поэтому naturalWidth/width ≈ naturalHeight/height. Для экранов со
    // скроллом бэкенд отдаёт размеры viewport'а, и по ним масштаб не вычислить.
    const [exportScale, setExportScale] = useState<number>(2);

    useEffect(() => {
        if (!testData) return;

        let cancelled = false;

        const detect = async () => {
            for (const screen of testData.screens) {
                if (!screen.imageUrl || !screen.width || !screen.height) continue;

                const size = await new Promise<{ width: number; height: number } | null>((resolve) => {
                    const img = new Image();
                    img.onload = () =>
                        resolve(img.naturalWidth > 0 ? { width: img.naturalWidth, height: img.naturalHeight } : null);
                    img.onerror = () => resolve(null);
                    img.src = screen.imageUrl;
                });
                if (!size) continue;

                const scaleX = size.width / screen.width;
                const scaleY = size.height / screen.height;
                if (scaleX > 0 && Math.abs(scaleX - scaleY) < 0.05) {
                    if (!cancelled) setExportScale(scaleX);
                    return;
                }
            }
        };

        detect();
        return () => {
            cancelled = true;
        };
    }, [testData]);

    const startMediaRecording = async (data: FigmaTestData) => {
        if (!data.recordScreen && !data.recordAudio && !data.recordCamera) return;

        const started: Array<{ recorder: MediaRecorder; blobParts: Array<Blob>; type: string }> = [];

        try {
            // getDisplayMedia не поддерживается мобильными браузерами (Chrome/Firefox на Android,
            // Safari на iOS) — там метода просто нет, и вызов мгновенно падает с TypeError.
            // На таких устройствах запись экрана пропускаем, остальные записи (камера/аудио)
            // работают как обычно.
            const canRecordScreen = typeof navigator.mediaDevices?.getDisplayMedia === 'function';

            if (data.recordScreen && !canRecordScreen) {
                console.warn('Figma test: screen recording is not supported on this device, skipping it');
            }

            if (data.recordScreen && canRecordScreen) {
                const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
                const recorder = new MediaRecorder(stream);
                const blobParts: Array<Blob> = [];
                recorder.ondataavailable = (e) => {
                    if (e.data.size > 0) blobParts.push(e.data);
                };
                recorder.onstop = () => {
                    // Если респондент остановил шеринг экрана до завершения теста,
                    // считаем это досрочным завершением. В режиме с целевым экраном
                    // такое действие приравнивается к сдаче.
                    if (!isCompletedRef.current) {
                        const screenId = currentScreenIdRef.current || data.startScreen;
                        const screen = data.screens.find((s) => s.id === screenId);
                        const isGoalBasedMode = Boolean(data.targetScreens && data.targetScreens.length > 0);
                        completeTestRef.current(isGoalBasedMode ? false : null, screenId, screen?.name || '');
                    }
                };
                recorder.start();
                started.push({ recorder, blobParts, type: 'screen' });
            }

            if (data.recordCamera) {
                // Если включены и камера, и аудио — записываем звук вместе с видео камеры,
                // отдельную аудиозапись не ведём.
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: data.recordAudio,
                });
                const recorder = new MediaRecorder(stream);
                const blobParts: Array<Blob> = [];
                recorder.ondataavailable = (e) => {
                    if (e.data.size > 0) blobParts.push(e.data);
                };
                recorder.start();
                started.push({ recorder, blobParts, type: 'camera' });
            }

            // Отдельную аудиозапись запускаем только если не включена камера
            // (при включенной камере аудио идёт в составе видео с камеры).
            if (data.recordAudio && !data.recordCamera) {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                const recorder = new MediaRecorder(stream);
                const blobParts: Array<Blob> = [];
                recorder.ondataavailable = (e) => {
                    if (e.data.size > 0) blobParts.push(e.data);
                };
                recorder.start();
                started.push({ recorder, blobParts, type: 'audio' });
            }

            mediaRecordersRef.current = started;
        } catch (e) {
            // При ошибке получения любого из разрешений останавливаем уже запущенные записи
            // и пробрасываем ошибку дальше — без разрешений дальше пройти нельзя.
            for (const item of started) {
                if (item.recorder.state !== 'inactive') {
                    item.recorder.stop();
                }
                item.recorder.stream.getTracks().forEach((track) => track.stop());
            }
            throw e;
        }
    };

    const stopMediaRecording = useCallback(async (): Promise<{
        videoFileId?: number;
        audioFileId?: number;
        cameraFileId?: number;
    }> => {
        const result: { videoFileId?: number; audioFileId?: number; cameraFileId?: number } = {};

        // Сначала останавливаем все записи и готовим файлы для загрузки.
        const filesToUpload: Array<{ file: File; type: string }> = [];

        for (const item of mediaRecordersRef.current) {
            if (item.recorder.state !== 'inactive') {
                await new Promise<void>((resolve) => {
                    item.recorder.onstop = () => resolve();
                    item.recorder.stop();
                });
            }

            // Останавливаем все треки
            item.recorder.stream.getTracks().forEach((track) => track.stop());

            const blob = new Blob(item.blobParts, { type: item.recorder.mimeType });
            if (blob.size === 0) continue;

            const file = new File([blob], `figma-test-${item.type}-${Date.now()}.webm`, { type: blob.type });
            filesToUpload.push({ file, type: item.type });
        }

        // Загружаем все файлы одним запросом, как рекомендует бэкенд.
        if (filesToUpload.length > 0) {
            try {
                const uploaded = await surveyApi.uploadFilesBatch(
                    questionId,
                    filesToUpload.map((item) => item.file),
                );

                uploaded.forEach((uploadedFile, index) => {
                    const type = filesToUpload[index]?.type;
                    const fileId = (uploadedFile as { file: { id: number } }).file.id;

                    if (type === 'screen') {
                        result.videoFileId = fileId;
                    } else if (type === 'audio') {
                        result.audioFileId = fileId;
                    } else if (type === 'camera') {
                        result.cameraFileId = fileId;
                    }
                });
            } catch (e) {
                console.error('Upload error:', e);
            }
        }

        mediaRecordersRef.current = [];
        return result;
    }, [questionId]);

    const getTimestamp = () => Date.now() - startTimeRef.current;

    const completeTest = useCallback(
        async (success: boolean | null, finalScreenId: string, finalScreenName: string, expired = false) => {
            if (isCompletedRef.current) return;
            isCompletedRef.current = true;
            setIsCompleted(true);
            setIsFullscreen(false);
            setIsSavingMedia(true);

            timer?.send({ type: 'STOP' });

            const timeMs = getTimestamp();
            const mediaResult = await stopMediaRecording();

            setIsSavingMedia(false);

            if (expired) {
                setCompletionStatus('timeout');
            } else if (success === true) {
                setCompletionStatus('success');
            } else if (success === false) {
                setCompletionStatus('surrender');
            } else {
                setCompletionStatus('complete');
            }

            const endEvent: FigmaEvent = {
                type: expired ? 'surrender' : success === false ? 'surrender' : 'end',
                screenId: finalScreenId,
                screenName: finalScreenName,
                timestampMs: timeMs,
            };

            const finalEvents = [...eventsRef.current, endEvent];
            const finalPath = [...pathRef.current, { screen: finalScreenId, name: finalScreenName }];

            setEvents(finalEvents);
            setPath(finalPath);

            // Сохраняем ответ сразу после завершения, не откладывая на 2 секунды.
            // Плашка завершения уже показана выше, поэтому задержка только мешала
            // отправке опроса с первого раза.
            actor.send({
                type: 'QUESTION_RESPONSE_CHANGE',
                value: {
                    success,
                    timeMs,
                    finalScreenName,
                    events: finalEvents,
                    path: finalPath,
                    expired,
                    videoFileId: mediaResult.videoFileId ?? null,
                    audioFileId: mediaResult.audioFileId ?? null,
                    cameraFileId: mediaResult.cameraFileId ?? null,
                } as unknown as PrimitiveValue,
                questionId,
            });

            // Принудительно обновляем флаги валидности, чтобы кнопка «Отправить»
            // сработала с первого раза.
            actor.send({ type: 'VALIDATE' });

            // Если на странице только этот обязательный FIGMA-вопрос, кнопки
            // «Далее»/«Отправить» скрыты — переходим дальше автоматически через 1 секунду.
            const parentRef = actor.getSnapshot().context.parentRef;
            const surveyCtx = parentRef ? ((parentRef.getSnapshot() as any).context as SurveyStore) : null;
            const currentBundle = surveyCtx ? selectCurrentBundle(surveyCtx) : null;

            if (!readOnly && question.params.required && currentBundle?.questions.length === 1) {
                autoAdvanceTimeoutRef.current = setTimeout(() => {
                    parentRef?.send({ type: 'SELECT_NEXT_BUNDLE', noValidate: false });
                }, 1000);
            }
        },
        [questionId, actor, stopMediaRecording, timer, readOnly, question.params.required],
    );

    useEffect(() => {
        completeTestRef.current = completeTest;
    }, [completeTest]);

    const handleTimerExpired = useCallback(() => {
        if (!testData || !currentScreenId || !hasStarted || isCompleted) return;
        const screen = testData.screens.find((s) => s.id === currentScreenId);
        completeTest(false, currentScreenId, screen?.name || '', true);
    }, [testData, currentScreenId, hasStarted, isCompleted, completeTest]);

    useEffect(() => {
        handleTimerExpiredRef.current = handleTimerExpired;
    }, [handleTimerExpired]);

    const handleSurrender = useCallback(() => {
        if (!testData || !currentScreenId || !hasStarted || isCompleted) return;
        const screen = testData.screens.find((s) => s.id === currentScreenId);
        completeTest(false, currentScreenId, screen?.name || '', false);
    }, [testData, currentScreenId, hasStarted, isCompleted, completeTest]);

    const handleComplete = useCallback(() => {
        if (!testData || !currentScreenId || !hasStarted || isCompleted) return;
        const screen = testData.screens.find((s) => s.id === currentScreenId);
        completeTest(null, currentScreenId, screen?.name || '', false);
    }, [testData, currentScreenId, hasStarted, isCompleted, completeTest]);

    const hotspots = useMemo(() => {
        if (!testData) return [];
        return transitionsToHotspots(testData.transitions);
    }, [testData]);

    /**
     * Обработка клика по hotspot, полученного через Figma Embed Kit 2.0 (postMessage).
     * Фиксирует клик; переход фиксируется в handleEmbedScreenChange по событию PRESENTED_NODE_CHANGED.
     */
    const handleEmbedHotspotClick = useCallback(
        (hotspot: FigmaHotspot, clickX: number, clickY: number, screenIdForClick: string | null) => {
            if (!testData || !screenIdForClick || !hasStarted || isCompleted || readOnly) return;

            const currentScreen = testData.screens.find((s) => s.id === screenIdForClick);
            const timestampMs = getTimestamp();

            const clickEvent: FigmaEvent = {
                type: 'click',
                screenId: screenIdForClick,
                screenName: currentScreen?.name,
                x: Math.round(clickX),
                y: Math.round(clickY),
                timestampMs,
            };

            setEvents((prev) => [...prev, clickEvent]);

            // Запоминаем факт клика по экрану, чтобы fallback в handleEmbedScreenChange
            // не добавлял фейковый клик сразу после реального.
            lastClickOnScreenRef.current = { screenId: screenIdForClick, timestamp: Date.now() };

            // Запоминаем факт клика по переходу, чтобы handleEmbedScreenChange не дублировал
            // событие click и мог использовать реальные координаты.
            if (hotspot.target) {
                clickRecordedForTransitionRef.current = {
                    toScreen: hotspot.target,
                    clickX,
                    clickY,
                };
            }
        },
        [testData, hasStarted, isCompleted, readOnly],
    );

    const handleEmbedEmptyAreaClick = useCallback(
        (clickX: number, clickY: number, screenIdForClick: string | null) => {
            if (!testData || !screenIdForClick || !hasStarted || isCompleted || readOnly) return;

            const currentScreen = testData.screens.find((s) => s.id === screenIdForClick);
            const timestampMs = getTimestamp();

            const emptyClickEvent: FigmaEvent = {
                type: 'click',
                screenId: screenIdForClick,
                screenName: currentScreen?.name,
                x: Math.round(clickX),
                y: Math.round(clickY),
                timestampMs,
            };

            setEvents((prev) => [...prev, emptyClickEvent]);

            lastClickOnScreenRef.current = { screenId: screenIdForClick, timestamp: Date.now() };
        },
        [testData, hasStarted, isCompleted, readOnly],
    );

    const handleEmbedScreenChange = useCallback(
        (figmaNodeId: string) => {
            if (!testData || !figmaNodeId || !hasStarted) return;

            // Ищем экран по figmaNodeId (screen.id совпадает с figmaNodeId).
            const nextScreen = testData.screens.find((s) => s.figmaNodeId === figmaNodeId || s.id === figmaNodeId);
            if (!nextScreen || nextScreen.id === currentScreenId) return;

            const currentScreen = testData.screens.find((s) => s.id === currentScreenId);
            const timestampMs = getTimestamp();

            // Определяем координаты клика, вызвавшего переход.
            // Если клик по hotspot уже зафиксирован — используем его координаты.
            // Иначе ищем переход по текущему экрану и берём центр hotspot.
            const transition = testData.transitions.find(
                (t) =>
                    t.fromScreen === currentScreenId &&
                    (t.toScreen === nextScreen.id || t.toScreen === nextScreen.figmaNodeId),
            );

            let clickX: number | undefined;
            let clickY: number | undefined;
            let clickAlreadyRecorded = false;

            if (clickRecordedForTransitionRef.current?.toScreen === nextScreen.id) {
                clickX = clickRecordedForTransitionRef.current.clickX;
                clickY = clickRecordedForTransitionRef.current.clickY;
                clickAlreadyRecorded = true;
                clickRecordedForTransitionRef.current = null;
            } else if (transition) {
                // Добавляем fallback-клик только если на текущем экране не было
                // реального клика непосредственно перед этим переходом.
                const lastClick = lastClickOnScreenRef.current;
                const hasRecentRealClick =
                    lastClick?.screenId === currentScreenId && Date.now() - lastClick.timestamp < 500;

                if (!hasRecentRealClick) {
                    // Реальные клики хранятся в процентах от экспортированного
                    // изображения экрана (см. FigmaEmbedViewer) — fallback-клик
                    // приводим к той же системе, иначе в результатах он рисуется
                    // не там (hotspot задан в дизайн-пикселях контента).
                    const hotspotCenterX = transition.hotspot.x + transition.hotspot.width / 2;
                    const hotspotCenterY = transition.hotspot.y + transition.hotspot.height / 2;
                    // Приоритет — размеры контента из плагина; для старых прототипов
                    // выводим их из натуральных размеров картинки и exportScale.
                    const percentBaseX =
                        currentScreen?.contentWidth && currentScreen.contentWidth > 0
                            ? currentScreen.contentWidth
                            : screenImageSize && exportScale > 0
                            ? screenImageSize.width / exportScale
                            : prototypeSize.width || 1;
                    const percentBaseY =
                        currentScreen?.contentHeight && currentScreen.contentHeight > 0
                            ? currentScreen.contentHeight
                            : screenImageSize && exportScale > 0
                            ? screenImageSize.height / exportScale
                            : prototypeSize.height || 1;
                    clickX = (hotspotCenterX / percentBaseX) * 100;
                    clickY = (hotspotCenterY / percentBaseY) * 100;
                }
            }

            if (!clickAlreadyRecorded && typeof clickX === 'number' && typeof clickY === 'number') {
                const clickEvent: FigmaEvent = {
                    type: 'click',
                    screenId: currentScreenId,
                    screenName: currentScreen?.name,
                    x: Math.round(clickX),
                    y: Math.round(clickY),
                    timestampMs,
                };
                setEvents((prev) => [...prev, clickEvent]);
            }

            const transitionEvent: FigmaEvent = {
                type: 'transition',
                screenId: nextScreen.id,
                screenName: nextScreen.name,
                timestampMs,
            };

            setEvents((prev) => [...prev, transitionEvent]);
            setPath((prev) => [...prev, { screen: nextScreen.id, name: nextScreen.name || '' }]);
            setCurrentScreenId(nextScreen.id);

            // Проверяем достижение цели (goal-based)
            if (isGoalBased && testData.targetScreens?.includes(nextScreen.id)) {
                completeTest(true, nextScreen.id, nextScreen.name || '');
            }
        },
        [testData, currentScreenId, hasStarted, isGoalBased, completeTest, prototypeSize, screenImageSize, exportScale],
    );

    const handleStart = useCallback(async () => {
        if (!testData) return;
        setPermissionsError(null);
        setIsRequestingPermissions(true);

        try {
            await startMediaRecording(testData);
            setHasStarted(true);
            setIsFullscreen(true);
            startTimeRef.current = Date.now();

            const startScreen = testData.screens.find((s) => s.id === testData.startScreen);
            setEvents([
                {
                    type: 'start',
                    screenId: testData.startScreen,
                    screenName: startScreen?.name,
                    timestampMs: 0,
                },
            ]);
            setPath([{ screen: testData.startScreen, name: startScreen?.name || '' }]);

            const timerDuration = timer?.getSnapshot().context.duration;
            if (timer && timerDuration && timerDuration > 0) {
                pendingTimerStartRef.current = true;
            }
        } catch (e) {
            console.error('Media permissions error:', e);
            setPermissionsError(a.figmaPermissionsError);
            await stopMediaRecording();
        } finally {
            setIsRequestingPermissions(false);
        }
    }, [testData, a, stopMediaRecording, timer]);

    const getStartInstructionMediaText = () => {
        if (!testData) return '';
        const { recordScreen, recordAudio, recordCamera } = testData;
        if (recordScreen && recordAudio && recordCamera) return a.figmaStartInstructionScreenAudioCamera;
        if (recordScreen && recordAudio) return a.figmaStartInstructionScreenAudio;
        if (recordScreen && recordCamera) return a.figmaStartInstructionScreenCamera;
        if (recordAudio && recordCamera) return a.figmaStartInstructionAudioCamera;
        if (recordScreen) return a.figmaStartInstructionScreen;
        if (recordAudio) return a.figmaStartInstructionAudio;
        if (recordCamera) return a.figmaStartInstructionCamera;
        return '';
    };

    if (isLoading) {
        return <div className={css.figmaTest}>{a.figmaLoadingText}</div>;
    }

    if (error) {
        return <div className={css.figmaTest}>{error}</div>;
    }

    if (!testData || !currentScreenId) {
        return <div className={css.figmaTest}>{a.figmaNoDataText}</div>;
    }

    const currentScreen = testData.screens.find((s) => s.id === currentScreenId);

    if (!currentScreen) {
        return <div className={css.figmaTest}>{a.figmaScreenNotFoundText}</div>;
    }

    if (!testData.figmaFileId || !testData.figmaStartNodeId) {
        return <div className={css.figmaTest}>{a.figmaPrototypeUnavailableText}</div>;
    }

    // Базовые ограничения:
    // - по ширине: iframe не шире контейнера вопроса;
    // - по высоте: iframe не выше viewport (window.innerHeight), с запасом для UI.
    const windowWidth = typeof window !== 'undefined' ? window.innerWidth : DEFAULT_PROTOTYPE_WIDTH;
    const containerWidth = isFullscreen ? windowWidth : containerSize.width || windowWidth - 40;
    const windowHeight = typeof window !== 'undefined' ? window.innerHeight : DEFAULT_PROTOTYPE_HEIGHT;
    // В fullscreen оставляем место под фиксированную панель действий
    // (.actionButtons: padding 20px + кнопка 40px), чтобы прототип не уходил под неё.
    const fullscreenReservedHeight = isFullscreen ? 80 : 0;
    const viewportHeight = isFullscreen
        ? Math.max(200, windowHeight - fullscreenReservedHeight)
        : Math.max(200, windowHeight - 250);

    // В режиме превью ограничиваем высоту, чтобы задание не занимало весь экран
    // до нажатия кнопки «Начать».
    const previewMaxHeight = Math.min(500, Math.round(windowHeight * 0.8));

    let embedWidth: number;
    let embedHeight: number;
    let embedScale: number;

    if (testData.displayMode === 'fitWidth') {
        // Растягиваем по ширине контейнера, но не больше натурального размера
        // прототипа: embed с scaling=scale-down-width апскейл не делает —
        // прототип рисуется в натуральную величину по центру iframe. Если
        // посчитать iframe как растянутый до ширины (прежняя формула), он
        // оказывается в разы больше реального рендера: вокруг чёрные поля, а
        // прототип «где-то ниже» (#1200).
        embedScale = Math.min(1, containerWidth / prototypeSize.width);
        embedWidth = Math.round(prototypeSize.width * embedScale);
        embedHeight = Math.round(prototypeSize.height * embedScale);
    } else if (testData.displayMode === 'fitHeight') {
        // Вписываем по высоте в viewport, ширина может вылезать со скроллом.
        embedScale = Math.min(1, viewportHeight / prototypeSize.height);
        embedHeight = Math.round(prototypeSize.height * embedScale);
        embedWidth = Math.round(prototypeSize.width * embedScale);
    } else {
        // original — показываем в натуральном размере прототипа
        embedWidth = prototypeSize.width;
        embedHeight = prototypeSize.height;
        embedScale = 1;
    }

    // В fullscreen вписываем прототип в высоту экрана (за вычетом панели действий),
    // чтобы низ макета не уходил под кнопку и его не приходилось искать прокруткой.
    // Это касается и fitWidth: мобильный макет (375×812) на ноутбуке при масштабе 1
    // выше видимой области, и раньше он обрезался снизу. Уменьшаем и iframe, и
    // масштаб — Figma масштабирует содержимое под размер iframe, поэтому картинка
    // остаётся пропорциональной и не обрезается.
    // На координаты кликов это не влияет: Figma присылает их в дизайн-пикселях
    // относительно ближайшего scrolling frame.
    if (isFullscreen && embedHeight > viewportHeight) {
        const fullscreenScale = viewportHeight / embedHeight;
        embedHeight = viewportHeight;
        embedWidth = Math.round(embedWidth * fullscreenScale);
        embedScale = embedScale * fullscreenScale;
    }

    // В режиме превью уменьшаем прототип, чтобы он поместился в 80vh.
    if (readOnly && !isFullscreen && embedHeight > previewMaxHeight) {
        const previewScale = previewMaxHeight / embedHeight;
        embedHeight = previewMaxHeight;
        embedWidth = Math.round(embedWidth * previewScale);
        embedScale = embedScale * previewScale;
    }

    const instructionContent = (
        <>
            <div className={css.instructionTitle}>{a.figmaStartTitle}</div>
            {!readOnly && (testData.recordScreen || testData.recordAudio || testData.recordCamera) ? (
                <>
                    <div className={css.instructionText}>{getStartInstructionMediaText()}</div>
                    {permissionsError && (
                        <>
                            <div className={css.instructionError}>{permissionsError}</div>
                            <div className={css.instructionHint}>{a.figmaPermissionsManualText}</div>
                        </>
                    )}
                </>
            ) : (
                <div className={css.instructionText}>{a.figmaStartInstruction}</div>
            )}
            <Button
                fluid
                color='blue'
                darkText={darkText}
                className={css.startButton}
                onClick={handleStart}
                disabled={isRequestingPermissions || readOnly}
            >
                {isRequestingPermissions ? a.figmaStartButtonRequesting : a.figmaStartButton}
            </Button>
        </>
    );

    return (
        <div
            ref={containerRef}
            className={cn(css.figmaTest, CssQuestionType.QUESTION, isFullscreen && css.figmaTestFullscreen)}
            style={{ textAlign: questionBlockPosition }}
        >
            {readOnly ? (
                <>
                    <div className={css.previewWrapper}>
                        <div className={css.previewImageWrap}>
                            <img src={currentScreen.imageUrl} alt={currentScreen.name} className={css.previewImage} />
                        </div>
                    </div>
                    {!isCompleted && !hasStarted && !isEmbedLoading && (
                        <div className={css.instructionOverlay}>{instructionContent}</div>
                    )}
                </>
            ) : !hasStarted && !isCompleted ? (
                <div className={css.instructionBlock}>{instructionContent}</div>
            ) : (
                <>
                    <FigmaEmbedViewer
                        key={`figma-embed-${testData.figmaFileId}`}
                        fileId={testData.figmaFileId}
                        nodeId={testData.figmaStartNodeId}
                        fileName={testData.figmaFileName || 'Figma Prototype'}
                        hotspots={hotspots}
                        currentScreen={currentScreenId}
                        nestedFrames={currentScreen.nestedFrames}
                        width={embedWidth}
                        height={embedHeight}
                        scale={embedScale}
                        originalWidth={prototypeSize.width}
                        originalHeight={prototypeSize.height}
                        contentWidth={currentScreen.contentWidth}
                        contentHeight={currentScreen.contentHeight}
                        screenImageWidth={screenImageSize?.width}
                        screenImageHeight={screenImageSize?.height}
                        exportScale={exportScale}
                        onHotspotClick={handleEmbedHotspotClick}
                        onEmptyAreaClick={handleEmbedEmptyAreaClick}
                        onScreenChange={handleEmbedScreenChange}
                        onLoad={() => {
                            setIsEmbedLoading(false);
                            // Отсчёт времени прохождения (timeMs в результатах) ведём
                            // от реальной загрузки прототипа (INITIAL_LOAD Embed Kit),
                            // а не от клика по «Начать» — иначе в результат попадает
                            // время загрузки iframe, и оно расходится с таймером вопроса.
                            startTimeRef.current = Date.now();
                            // Пока прототип грузился, фуллскрин можно было прокрутить
                            // (виден только чёрный фон). Возвращаем контейнер к началу,
                            // чтобы прототип оказался перед глазами сразу после загрузки (#1200).
                            if (isFullscreen) {
                                containerRef.current?.scrollTo({ top: 0 });
                            }
                            if (pendingTimerStartRef.current) {
                                pendingTimerStartRef.current = false;
                                timer?.send({ type: 'START' });
                            }
                        }}
                        debugOverlayEnabled={false}
                        hideUI
                        hotspotHints={false}
                        scaling={
                            testData.displayMode === 'fitWidth'
                                ? 'scale-down-width'
                                : testData.displayMode === 'fitHeight'
                                ? 'scale-down-width'
                                : 'scale-down'
                        }
                        contentScaling='fixed'
                        maxWidth={testData.displayMode === 'fitWidth' ? '100%' : 'none'}
                        maxHeight={testData.displayMode === 'fitHeight' ? `${windowHeight}px` : undefined}
                        bgColor='000000'
                        figmaClientId={FIGMA_CLIENT_ID}
                    />
                    <div className={css.screenName}>{currentScreen.name}</div>
                    {isEmbedLoading && !isCompleted && (
                        <div className={css.embedLoader}>
                            <Loader visible inline className={css.embedLoader__loader} />
                            {a.figmaLoadingText}
                        </div>
                    )}
                </>
            )}

            {hasStarted && !isCompleted && (
                <div className={css.actionButtons}>
                    {timerLeft !== null && timerLeft > 0 && isFullscreen && (
                        <TimeLeft
                            className={css.timerBottom}
                            color={elementColor}
                            value={timerLeftDuration}
                            borderRadius={buttonBorderRadius ? `${buttonBorderRadius}px` : undefined}
                        />
                    )}
                    {isGoalBased ? (
                        <Button
                            fluid
                            color='blue'
                            darkText={darkText}
                            className={css.actionButton}
                            onClick={handleSurrender}
                        >
                            {a.figmaSurrenderButton}
                        </Button>
                    ) : (
                        <Button
                            fluid
                            color='blue'
                            darkText={darkText}
                            className={css.actionButton}
                            onClick={handleComplete}
                        >
                            {a.figmaCompleteButton}
                        </Button>
                    )}
                </div>
            )}

            {isSavingMedia && (
                <div className={css.savingMediaOverlay}>
                    <Loader visible inline className={css.savingMediaLoader} />
                    <div className={css.savingMediaText}>{a.figmaSavingMediaText}</div>
                </div>
            )}

            {isCompleted && completionStatus && (
                <div className={cn(css.completionOverlay, css[`completionOverlay--${completionStatus}`])}>
                    <div className={css.completionCard}>
                        {completionStatus === 'success' && (
                            <>
                                <div className={css.completionTitle}>{a.figmaCompletionSuccessTitle}</div>
                                <div className={css.completionText}>{a.figmaCompletionSuccessText}</div>
                            </>
                        )}
                        {completionStatus === 'surrender' && (
                            <>
                                <div className={css.completionTitle}>{a.figmaCompletionSurrenderTitle}</div>
                                <div className={css.completionText}>{a.figmaCompletionSurrenderText}</div>
                            </>
                        )}
                        {completionStatus === 'timeout' && (
                            <>
                                <div className={css.completionTitle}>{a.figmaCompletionTimeoutTitle}</div>
                                <div className={css.completionText}>{a.figmaCompletionTimeoutText}</div>
                            </>
                        )}
                        {completionStatus === 'complete' && (
                            <>
                                <div className={css.completionTitle}>{a.figmaCompletionCompleteTitle}</div>
                                <div className={css.completionText}>{a.figmaCompletionCompleteText}</div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
